import React, {FC, Fragment, useMemo} from "react"
import {useCashbackTransactionList} from "@irati/cashback/hooks/cashback"
import authentication from "@irati/auth"
import {CashbackTransactionStatus} from "@irati/types"
import {CurrencyType} from "@irati/payments/enums"
import StatusType from "@irati/wallet/types/enums/Status"
import StatementItemTypes from "@irati/wallet/types/enums/StatementItemTypes"
import Flex from "@irati/ui/src/Flex"
import {Body} from "@irati/ui/src/Typography"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import {useTheme} from "@irati/ui/src/global/useTheme"
import Badge from "@irati/ui/src/Badge"
import Loading from "@irati/ui/src/Loading"
import groupByDate from "@irati/wallet/helpers/groupWalletItemsByDate"
import Spacing from "@irati/ui/src/Spacing"
import {OverlayPlacement, Tooltip} from "@irati/ui/src/Tooltip"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import {ButtonLink} from "@irati/ui/src/Link"
import DateHeader from "../../StatementItems/DateHeader"
import {Hr, ShowMoreButton, StatementItem as StatementItemComponent} from "../../StatementItems/styled"
import NoData from "../../NoData"

interface Props {
  status: CashbackTransactionStatus
  showZeroState?: boolean
}

const Transactions: FC<Props> = ({status, showZeroState}) => {
  const {formatMessage} = useIntl()
  const {colors} = useTheme()

  const {
    data: transactions,
    load,
    fetchMore
  } = useCashbackTransactionList({
    filters: {
      userId: authentication.user().uuid!,
      status
    },
    limit: 10
  })

  const handleShowMore = () => {
    fetchMore({
      variables: {
        searchAfter: transactions?.nextSearchAfter,
        filters: {
          userId: authentication.user().uuid!,
          status
        }
      },
      updateQuery: (prev: any, {fetchMoreResult}) => {
        if (!fetchMoreResult) return prev

        return {
          cashbackTransactionsList: {
            ...prev.cashbackTransactionsList,
            results: [...prev.cashbackTransactionsList.results, ...fetchMoreResult.cashbackTransactionsList.results],
            nextSearchAfter: fetchMoreResult.cashbackTransactionsList.nextSearchAfter
          }
        }
      }
    })
  }

  const items = useMemo(
    () =>
      transactions?.results?.map((item) => {
        return {
          statementItem: {
            uuid: item.uuid,
            transfer: {
              amount: item.amount,
              currency: CurrencyType.GBP,
              createdAt: new Date(item?.dateUnix * 1000).toISOString() // Convert to ISO string for consistency
            },
            content: {
              title: {
                text: item.deal?.header
              },
              subTitle: {
                text:
                  status === CashbackTransactionStatus.StatusPending ? (
                    <Flex>
                      <Badge padding={[0, 1]} bgColor={colors.primary2} color={colors.greys.white}>
                        <FormattedMessage id="wallet.walletBalanceCard.pending.header" />
                      </Badge>
                    </Flex>
                  ) : (
                    "wallet.statementItem.adminReward.transfer.subTitle"
                  )
              },
              status: status === CashbackTransactionStatus.StatusPending && StatusType.CASHBACK,
              type: StatementItemTypes.BE_SIMPLE_STATEMENT,
              image: item.deal?.thumbnailImage
            }
          },
          customDetails: (
            <Flex direction="column" gap={2} flex="1">
              <Flex gap={1} alignItems="center" justifyContent="space-between">
                {status === CashbackTransactionStatus.StatusPending ? (
                  <Body small>
                    {item.deal?.cashbackETADays ? (
                      <FormattedMessage
                        id="wallet.cashback.statement.pending.customDetails.text"
                        values={{
                          cashbackETADays: (
                            <Body weight="bold" display="inline">
                              <FormattedMessage
                                id="wallet.cashback.statement.pending.customDetails.cashbackETADays"
                                values={{cashbackETADays: item.deal?.cashbackETADays}}
                              />
                            </Body>
                          )
                        }}
                      />
                    ) : (
                      <FormattedMessage id="perks.components.cashbackRedemptionInstructions.alert.subTitleZeroDays" />
                    )}
                  </Body>
                ) : (
                  <Body small>
                    <FormattedMessage id="wallet.cashback.statement.rejected.customDetails.text" />
                  </Body>
                )}

                {status !== CashbackTransactionStatus.StatusPending && (
                  <ButtonLink
                    tertiary
                    href={formatMessage({id: "wallet.cashback.statement.rejected.customDetails.faq.href"})}
                    target="_blank">
                    <Flex alignItems="center">
                      <FormattedMessage id="wallet.cashback.statement.rejected.customDetails.faq.cta" />
                      <Icon icon={IconTypes.chevronRight} marginLeft />
                    </Flex>
                  </ButtonLink>
                )}
              </Flex>
            </Flex>
          )
        }
      }),
    [transactions]
  )

  const groupedData = groupByDate([], items)

  if ((!transactions || transactions.results?.length === 0) && !showZeroState) return null
  if ((!transactions || transactions.results?.length === 0) && showZeroState) {
    return <NoData useCardStyle text={formatMessage({id: "wallet.cashback.statement.rejected.zeroState.text"})} />
  }

  return (
    <Loading load={load}>
      {status === CashbackTransactionStatus.StatusPending && (
        <Flex gap={1.5} alignItems="center">
          <Body large>
            <FormattedMessage id="wallet.cashback.statement.pending.title" />
          </Body>
          <Tooltip
            placement={OverlayPlacement.Top}
            intl={{}}
            tooltipChildren={<FormattedMessage id="wallet.cashback.statement.pending.tooltip" />}>
            <Icon icon={IconTypes.circleQuestion} />
          </Tooltip>
        </Flex>
      )}
      <Flex direction="column">
        {groupedData.results.map(({data, date}, i) => {
          const key = date.format("YYYY-MM-DD")
          return (
            <Fragment key={key}>
              {i === 0 ? <Spacing marginTop={2} /> : <Hr flush />}
              <DateHeader date={date} />
              {data.map((item) => (
                <StatementItemComponent
                  id={`points-request-item_${item.statementItem.uuid}`}
                  statementItem={item.statementItem}
                  customDetails={item.customDetails}
                  key={item.statementItem.uuid}
                />
              ))}
            </Fragment>
          )
        })}
        {transactions?.nextSearchAfter && (
          <ShowMoreButton onClick={handleShowMore}>
            <FormattedMessage id="platform.components.showMore.showMore" />
          </ShowMoreButton>
        )}
      </Flex>
    </Loading>
  )
}

export {Transactions}
